import { Form, Formik } from "formik";
import * as Yup from "yup";
import CurrencyInput from "../../../Form/CurrencyInput";
import { useContext, useState } from "react";
import { useTranslation } from 'react-i18next';
import { CaixaContext } from "../../../../contexts/caixaContext";
import { convertPrice } from "../../../../utils/convertPrice";
import { formatPrice } from "../../../../utils/formatPrice";
import { formatPriceWithSymbol, formatAmountForInput } from "../../../../utils/currency";

import "./modern-style.css";
import { fecharCaixa, addLancamentoCaixa } from "../../../../services/api";
import { AuthContext } from "../../../../contexts/auth";
import { toast } from "react-toastify";

// Icons
import { FiDollarSign, FiCreditCard, FiSmartphone, FiMinus, FiCheck, FiX } from "react-icons/fi";
import { FaCalculator } from "react-icons/fa";
import { HiOutlineCash } from "react-icons/hi";

const CloseCaixaForm = ({ onClose }) => {
  const { t } = useTranslation('cashRegister');

  const {
    caixa,
    caixaValue,
    refetchStatus,
    dinheiroValue,
    cartaoValue,
    pixValue,
  } = useContext(CaixaContext);

  const [isSenting, setIsSenting] = useState(false);

  const validationSchema = Yup.object({
    valor_informado_cartao: Yup.string().required(t('closeCaixa.validation.cardRequired')),
    valor_informado_dinheiro: Yup.string().required(t('closeCaixa.validation.cashRequired')),
    valor_informado_pix: Yup.string().required(t('closeCaixa.validation.pixRequired')),
  });

  const { user } = useContext(AuthContext);
  const initialValues = {
    value: "",
    withdraw: "",
    valor_informado_dinheiro: "",
    valor_informado_cartao: "",
    valor_informado_pix: "",
  };

  const handleSubmit = async (values) => {
    const {
      withdraw,
      valor_informado_dinheiro,
      valor_informado_cartao,
      valor_informado_pix,
    } = values;

    try {
      setIsSenting(true);

      // Se há retirada, registrar como lançamento antes de fechar o caixa
      const valorRetirada = convertPrice(withdraw);
      if (valorRetirada > 0) {
        await addLancamentoCaixa(
          caixa.id_empresa,
          caixa._id,
          "Retirada no fechamento do caixa",
          "Dinheiro",
          -valorRetirada, // Valor negativo para retirada
          user._id
        );
      }

      // O valor informado de dinheiro JÁ É o saldo final (após retiradas)
      const saldoFinalCalculado = convertPrice(valor_informado_dinheiro);

      // Fechar o caixa
      await fecharCaixa(
        caixa.id_empresa,
        caixa._id,
        user._id,
        saldoFinalCalculado, // Saldo que ficará no caixa para abertura seguinte
        convertPrice(valor_informado_dinheiro),
        convertPrice(valor_informado_cartao),
        convertPrice(valor_informado_pix)
      );

      onClose();
      refetchStatus();
    } catch (e) {
      if (e.response?.data?.msg) {
        toast(e.response.data.msg, { type: "error" });
      } else {
        toast(t('closeCaixa.toasts.error'), { type: "error" });
      }
    } finally {
      setIsSenting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, handleChange, values, setFieldValue }) => {
        // Calcular retirada para ajustar valor esperado de dinheiro
        const valorRetirada = convertPrice(values.withdraw);

        // Valor esperado de dinheiro ajustado pela retirada
        const dinheiroValueAjustado = dinheiroValue - valorRetirada;

        // Cálculos para validação (informado - esperado ajustado)
        // Positivo = sobra, Negativo = falta
        const pixDifference = convertPrice(values.valor_informado_pix) - pixValue;
        const dinheiroDifference = convertPrice(values.valor_informado_dinheiro) - dinheiroValueAjustado;
        const cartaoDifference = convertPrice(values.valor_informado_cartao) - cartaoValue;

        // O valor informado JÁ É o valor que fica no caixa (após retiradas)
        const ficandoNoCaixaDinheiro = convertPrice(values.valor_informado_dinheiro);
        const isNegativo = ficandoNoCaixaDinheiro < 0;

        // Total esperado ajustado
        const totalEsperadoAjustado = dinheiroValueAjustado + pixValue + cartaoValue;
        const totalInformado = convertPrice(values.valor_informado_pix) +
                               convertPrice(values.valor_informado_dinheiro) +
                               convertPrice(values.valor_informado_cartao);
        const totalDifference = totalInformado - totalEsperadoAjustado;

        return (
          <Form className="modern-close-form">
            <div className="modern-payment-grid">
              {/* PIX Card */}
              <div className={`modern-payment-card ${
                pixDifference === 0 ? 'is-correct' :
                pixDifference !== 0 ? 'has-difference' : ''
              }`}>
                <div className="modern-payment-header">
                  <div className="modern-payment-icon">
                    <FiSmartphone />
                  </div>
                  <div className="modern-payment-info">
                    <h3 className="modern-payment-title">{t('payment.pix')}</h3>
                    <p className="modern-payment-subtitle">{t('payment.digitalPayments')}</p>
                  </div>
                </div>

                <div className="modern-input-group">
                  <label className="modern-input-label">{t('closeCaixa.expectedValue')}</label>
                  <input
                    className="modern-input"
                    disabled={true}
                    type="text"
                    value={formatPriceWithSymbol(pixValue)}
                  />
                </div>

                <div className="modern-input-group">
                  <label className="modern-input-label required">{t('closeCaixa.reportedValue')}</label>
                  <CurrencyInput
                    className="modern-input"
                    label="valor_informado_pix"
                    name="valor_informado_pix"
                    type="text"
                    placeholder={formatPriceWithSymbol(0)}
                  />
                </div>

                {values.valor_informado_pix && (
                  <div className={`modern-difference-indicator ${
                    pixDifference === 0 ? 'zero' :
                    pixDifference > 0 ? 'positive' : 'negative'
                  }`}>
                    {pixDifference === 0 ? (
                      <><FiCheck /> {t('closeCaixa.valuesMatch')}</>
                    ) : (
                      <><FiX /> {t('closeCaixa.difference', { amount: `${pixDifference > 0 ? '+' : ''}${formatPrice(pixDifference)}` })}</>
                    )}
                  </div>
                )}
              </div>
              {/* Dinheiro Card */}
              <div className={`modern-payment-card ${
                dinheiroDifference === 0 ? 'is-correct' :
                dinheiroDifference !== 0 ? 'has-difference' : ''
              }`}>
                <div className="modern-payment-header">
                  <div className="modern-payment-icon">
                    <HiOutlineCash />
                  </div>
                  <div className="modern-payment-info">
                    <h3 className="modern-payment-title">{t('payment.cash')}</h3>
                    <p className="modern-payment-subtitle">{t('payment.cashPayments')}</p>
                  </div>
                </div>

                <div className="modern-input-group">
                  <label className="modern-input-label">{t('closeCaixa.expectedValue')}</label>
                  <input
                    className="modern-input"
                    disabled={true}
                    type="text"
                    value={formatPriceWithSymbol(dinheiroValueAjustado)}
                    style={valorRetirada > 0 ? {backgroundColor: '#fff3cd', borderColor: '#ffeaa7'} : {}}
                  />
                  {valorRetirada > 0 && (
                    <small style={{color: '#856404', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                      {t('closeCaixa.originalMinusWithdrawal', { original: formatPriceWithSymbol(dinheiroValue), withdrawal: formatPriceWithSymbol(valorRetirada) })}
                    </small>
                  )}
                </div>

                <div className="modern-input-group">
                  <label className="modern-input-label required">{t('closeCaixa.reportedValue')}</label>
                  <CurrencyInput
                    className="modern-input"
                    label="valor_informado_dinheiro"
                    name="valor_informado_dinheiro"
                    type="text"
                    placeholder={formatPriceWithSymbol(0)}
                  />
                  {valorRetirada > 0 && !values.valor_informado_dinheiro && (
                    <button
                      type="button"
                      className="modern-suggestion-btn"
                      style={{
                        fontSize: '12px',
                        color: '#007bff',
                        background: 'none',
                        border: 'none',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        marginTop: '4px'
                      }}
                      // O campo espera o formato de DIGITACAO da moeda vigente; com a
                      // virgula cravada, clicar em "sugerir" numa loja USD preenchia
                      // "10,00" num campo que le "10.00".
                      onClick={() => setFieldValue('valor_informado_dinheiro', formatAmountForInput(dinheiroValueAjustado))}
                    >
                      {t('closeCaixa.suggestValue', { amount: formatPriceWithSymbol(dinheiroValueAjustado) })}
                    </button>
                  )}
                </div>

                {values.valor_informado_dinheiro && (
                  <div className={`modern-difference-indicator ${
                    dinheiroDifference === 0 ? 'zero' :
                    dinheiroDifference > 0 ? 'positive' : 'negative'
                  }`}>
                    {dinheiroDifference === 0 ? (
                      <><FiCheck /> {t('closeCaixa.valuesMatch')}</>
                    ) : (
                      <><FiX /> {t('closeCaixa.difference', { amount: `${dinheiroDifference > 0 ? '+' : ''}${formatPrice(dinheiroDifference)}` })}</>
                    )}
                  </div>
                )}
              </div>
              {/* Cartão Card */}
              <div className={`modern-payment-card ${
                cartaoDifference === 0 ? 'is-correct' :
                cartaoDifference !== 0 ? 'has-difference' : ''
              }`}>
                <div className="modern-payment-header">
                  <div className="modern-payment-icon">
                    <FiCreditCard />
                  </div>
                  <div className="modern-payment-info">
                    <h3 className="modern-payment-title">{t('payment.card')}</h3>
                    <p className="modern-payment-subtitle">{t('payment.debitCredit')}</p>
                  </div>
                </div>

                <div className="modern-input-group">
                  <label className="modern-input-label">{t('closeCaixa.expectedValue')}</label>
                  <input
                    className="modern-input"
                    disabled={true}
                    type="text"
                    value={formatPriceWithSymbol(cartaoValue)}
                  />
                </div>

                <div className="modern-input-group">
                  <label className="modern-input-label required">{t('closeCaixa.reportedValue')}</label>
                  <CurrencyInput
                    className="modern-input"
                    label="valor_informado_cartao"
                    name="valor_informado_cartao"
                    type="text"
                    placeholder={formatPriceWithSymbol(0)}
                  />
                </div>

                {values.valor_informado_cartao && (
                  <div className={`modern-difference-indicator ${
                    cartaoDifference === 0 ? 'zero' :
                    cartaoDifference > 0 ? 'positive' : 'negative'
                  }`}>
                    {cartaoDifference === 0 ? (
                      <><FiCheck /> {t('closeCaixa.valuesMatch')}</>
                    ) : (
                      <><FiX /> {t('closeCaixa.difference', { amount: `${cartaoDifference > 0 ? '+' : ''}${formatPrice(cartaoDifference)}` })}</>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* 3 Sections Layout - Side by side on large screens */}
            <div className="modern-sections-layout">
              {/* Summary Section */}
              <div className="modern-summary-section">
                <div className="modern-summary-header" style={{background: 'none'}}>
                  <div className="modern-summary-title">
                    <div className="modern-summary-icon">
                      <FaCalculator />
                    </div>
                    {t('closeCaixa.summaryTitle')}
                  </div>
                </div>
                <div className="modern-summary-content">
                  <div className="modern-summary-item">
                    <span className="modern-summary-label">{t('closeCaixa.expectedSystem')}</span>
                    <span className="modern-summary-value"
                          style={valorRetirada > 0 ? {color: '#856404'} : {}}>
                      {formatPriceWithSymbol(totalEsperadoAjustado)}
                    </span>
                  </div>
                  {valorRetirada > 0 && (
                    <div className="modern-summary-item" style={{fontSize: '12px', color: '#6c757d'}}>
                      <span className="modern-summary-label">{t('closeCaixa.originalMinusWithdrawal', { original: formatPriceWithSymbol(caixaValue), withdrawal: formatPriceWithSymbol(valorRetirada) })}</span>
                    </div>
                  )}
                  <div className="modern-summary-item">
                    <span className="modern-summary-label">{t('closeCaixa.totalReported')}</span>
                    <span className="modern-summary-value">{formatPriceWithSymbol(totalInformado)}</span>
                  </div>
                </div>

                {totalInformado > 0 && (
                  <div className={`modern-difference-indicator ${
                    totalDifference === 0 ? 'zero' :
                    totalDifference > 0 ? 'positive' : 'negative'
                  }`}>
                    {totalDifference === 0 ? (
                      <><FiCheck /> {t('closeCaixa.totalMatches')}</>
                    ) : (
                      <><FiX /> {t('closeCaixa.totalDifference', { amount: `${totalDifference > 0 ? '+' : ''}${formatPrice(totalDifference)}` })}</>
                    )}
                  </div>
                )}
              </div>

              {/* Withdraw Section */}
              <div className="modern-withdraw-section">
                <div className="modern-withdraw-header">
                  <div className="modern-withdraw-icon">
                    <FiMinus />
                  </div>
                  <h3 className="modern-withdraw-title">{t('closeCaixa.withdrawalTitle')}</h3>
                </div>
                <div className="modern-input-group">
                  <label className="modern-input-label">{t('closeCaixa.withdrawalLabel')}</label>
                  <CurrencyInput
                    className="modern-input"
                    label="withdraw"
                    name="withdraw"
                    type="text"
                    placeholder={formatPriceWithSymbol(0)}
                  />
                  {errors.withdraw && (
                    <div className="error" style={{color: 'var(--danger-color)', fontSize: '0.875rem', marginTop: '4px'}}>
                      {errors.withdraw}
                    </div>
                  )}

                  {isNegativo && (
                    <div style={{
                      backgroundColor: '#f8d7da',
                      color: '#721c24',
                      border: '1px solid #f5c6cb',
                      borderRadius: '4px',
                      padding: '8px 12px',
                      marginTop: '8px',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>⚠️</span>
                      <span>
                        <strong>{t('closeCaixa.negativeWarning')}</strong>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Final Summary */}
              <div className="modern-summary-section">
                <div className="modern-summary-header" style={{background: 'none'}}>
                  <div className="modern-summary-title">
                    <div className="modern-summary-icon">
                      <FiDollarSign />
                    </div>
                    {t('closeCaixa.finalValueTitle')}
                  </div>
                </div>
                <div className="modern-summary-content">
                  <div className="modern-summary-item">
                    <span className="modern-summary-label">{t('closeCaixa.registerTotal')}</span>
                    <span className="modern-summary-value large">
                      {formatPriceWithSymbol(totalInformado)}
                    </span>
                  </div>

                  <div className="modern-summary-item">
                    <span className="modern-summary-label">{t('closeCaixa.remainingCash')}</span>
                    <span className="modern-summary-value" style={{
                      color: isNegativo ? '#dc3545' : '#28a745',
                      fontWeight: 'bold'
                    }}>
                      {formatPriceWithSymbol(ficandoNoCaixaDinheiro)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="modern-modal-actions">
              <button
                className="modern-btn modern-btn-cancel"
                type="button"
                onClick={onClose}
              >
                {t('closeCaixa.cancel')}
              </button>
              <button
                className="modern-btn modern-btn-confirm"
                type="submit"
                disabled={isSenting || isNegativo}
                style={{
                  opacity: isNegativo ? 0.5 : 1,
                  cursor: isNegativo ? 'not-allowed' : 'pointer'
                }}
                title={isNegativo ? t('closeCaixa.tooltipNegative') : ''}
              >
                {isSenting ? (
                  <div className="modern-loading-container">
                    <div className="modern-loading-spinner"></div>
                    {t('closeCaixa.processing')}
                  </div>
                ) : (
                  <>
                    <FiCheck />
                    {isNegativo ? t('closeCaixa.negativeNotAllowed') : t('closeCaixa.confirmClose')}
                  </>
                )}
              </button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CloseCaixaForm;
